/**
 * Application configuration
 */
import type { ProjectConfig } from '/#/config';

import { APP_DARK_MODE_KEY_, PROJ_CFG_KEY } from '/@/enums/cacheEnum';
import projectSetting from '/@/settings/projectSetting';

import { updateHeaderBgColor, updateSidebarBgColor } from '/@/logics/theme/updateBackground';
import { updateColorWeak } from '/@/logics/theme/updateColorWeak';
import { updateGrayMode } from '/@/logics/theme/updateGrayMode';
import { updateDarkTheme } from '/@/logics/theme/dark';
import { changeTheme } from '/@/logics/theme';

import { useAppStore } from '/@/store/modules/app';
import { useLocaleStore } from '/@/store/modules/locale';

import { getCommonStoragePrefix, getStorageShortName } from '/@/utils/env';

import { primaryColor } from '../../build/config/themeConfig';
import { Persistent } from '/@/utils/cache/persistent';
import { deepMerge } from '/@/utils';
import { ThemeEnum } from '/@/enums/appEnum';

const LEGACY_DEFAULT_MENU_WIDTHS = [256, 232];

function normalizeLegacyProjectConfig(projCfg?: ProjectConfig | null): ProjectConfig | undefined {
  if (!projCfg) {
    return undefined;
  }

  const defaultMenuWidth = projectSetting.menuSetting?.menuWidth;
  const cachedMenuWidth = projCfg.menuSetting?.menuWidth;
  const isLegacyDefaultMenuWidth = typeof cachedMenuWidth === 'number' && LEGACY_DEFAULT_MENU_WIDTHS.includes(cachedMenuWidth);

  if (defaultMenuWidth && cachedMenuWidth !== defaultMenuWidth && isLegacyDefaultMenuWidth) {
    return {
      ...projCfg,
      menuSetting: {
        ...projCfg.menuSetting,
        menuWidth: defaultMenuWidth,
      },
    };
  }

  return projCfg;
}

// Initial project configuration
export function initAppConfigStore() {
  const localeStore = useLocaleStore();
  const appStore = useAppStore();
  let projCfg: ProjectConfig = Persistent.getLocal(PROJ_CFG_KEY) as ProjectConfig;
  projCfg = normalizeLegacyProjectConfig(projCfg) as ProjectConfig;
  projCfg = deepMerge(projectSetting, projCfg || {});
  localStorage.setItem(APP_DARK_MODE_KEY_, ThemeEnum.LIGHT);
  appStore.setDarkMode(ThemeEnum.LIGHT);
  const darkMode = ThemeEnum.LIGHT;
  projCfg = {
    ...projCfg,
    showDarkModeToggle: false,
    headerSetting: {
      ...projCfg.headerSetting,
      bgColor: projectSetting.headerSetting.bgColor,
      theme: ThemeEnum.LIGHT,
    },
    menuSetting: {
      ...projCfg.menuSetting,
      bgColor: projectSetting.menuSetting.bgColor,
      theme: ThemeEnum.LIGHT,
    },
  };
  const {
    colorWeak,
    grayMode,
    themeColor,

    headerSetting: { bgColor: headerBgColor } = {},
    menuSetting: { bgColor } = {},
  } = projCfg;
  try {
    if (themeColor && themeColor !== primaryColor) {
      changeTheme(themeColor);
    }

    grayMode && updateGrayMode(grayMode);
    colorWeak && updateColorWeak(colorWeak);
  } catch (error) {
    console.log(error);
  }
  appStore.setProjectConfig(projCfg);

  // init dark mode
  updateDarkTheme(darkMode);
  if (darkMode === ThemeEnum.DARK) {
    updateHeaderBgColor();
    updateSidebarBgColor();
  } else {
    headerBgColor && updateHeaderBgColor(headerBgColor);
    bgColor && updateSidebarBgColor(bgColor);
  }
  // init store
  localeStore.initLocale();

  setTimeout(() => {
    clearObsoleteStorage();
  }, 16);
}

/**
 * As the version continues to iterate, there will be more and more cache keys stored in localStorage.
 * This method is used to delete useless keys
 */
export function clearObsoleteStorage() {
  const commonPrefix = getCommonStoragePrefix();
  const shortPrefix = getStorageShortName();

  [localStorage, sessionStorage].forEach((item: Storage) => {
    Object.keys(item).forEach((key) => {
      if (key && key.startsWith(commonPrefix) && !key.startsWith(shortPrefix)) {
        item.removeItem(key);
      }
    });
  });
}
